import { useEffect, useRef, useState } from "react";

// Pointer-driven list reordering (mouse, pen and touch). The new order is
// committed once on drop via onCommit(orderedItems).
export function useDragOrder(items, onCommit) {
  const [rows, setRows] = useState(items);
  const [sourceIndex, setSourceIndex] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);
  const from = useRef(null);
  const to = useRef(null);
  const dragging = useRef(false);
  const rowsRef = useRef(items);
  const commit = useRef(onCommit);

  rowsRef.current = rows;
  commit.current = onCommit;

  // The parent may hand us a freshly built array on every render (e.g. a
  // filtered list). Only adopt it when the contents really changed, otherwise
  // this effect would loop forever and freeze the page it lives on.
  useEffect(() => {
    if (dragging.current) return;
    setRows((prev) =>
      prev.length === items.length && prev.every((p, i) => p === items[i]) ? prev : items,
    );
  }, [items]);

  useEffect(() => {
    const onUp = () => {
      if (!dragging.current) return;
      const src = from.current;
      const dst = to.current;
      dragging.current = false;
      from.current = null;
      to.current = null;
      setSourceIndex(null);
      setTargetIndex(null);
      if (src === null || dst === null || src === dst) return;
      const next = [...rowsRef.current];
      const [moved] = next.splice(src, 1);
      next.splice(dst, 0, moved);
      setRows(next);
      commit.current?.(next);
    };
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  return {
    rows,
    sourceIndex,
    targetIndex,
    handleProps: (index) => ({
      style: { touchAction: "none" },
      onPointerDown: (e) => {
        e.preventDefault();
        dragging.current = true;
        from.current = index;
        to.current = index;
        setSourceIndex(index);
        setTargetIndex(index);
      },
    }),
    rowProps: (index) => ({
      onPointerMove: () => {
        if (!dragging.current || to.current === index) return;
        to.current = index;
        setTargetIndex(index);
      },
    }),
  };
}
